/**
 * Hash routing, in about forty lines.
 *
 * A router dependency would buy nested layouts and data loaders this app does not
 * have, and would add a package to the licence gate for it. Hash routes also mean the
 * API can serve `index.html` for exactly one path and never learn the client's URL
 * shapes — the server's job is the API and one static bundle, nothing more.
 */

import { useEffect, useState } from "react";

export type Route =
  | { name: "datasets" }
  | { name: "workspace"; datasetId: string }
  | { name: "files"; datasetId: string }
  | { name: "table-profile"; datasetId: string; fileId: string }
  | { name: "search"; datasetId: string }
  | { name: "artifact"; datasetId: string; artifactId: string; versionId: string }
  | { name: "dossier"; datasetId: string; artifactId: string; versionId: string }
  | { name: "inference"; datasetId: string; artifactId: string; versionId: string }
  | { name: "settings" }
  | { name: "audit" };

export function parseRoute(hash: string): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  if (parts[0] === "settings") return { name: "settings" };
  if (parts[0] === "audit") return { name: "audit" };
  if (parts[0] === "d" && parts[1]) {
    const datasetId = parts[1];
    if (parts[2] === "files" && parts[3] === "table-profile" && parts[4]) {
      return { name: "table-profile", datasetId, fileId: parts[4] };
    }
    if (parts[2] === "files") return { name: "files", datasetId };
    if (parts[2] === "search") return { name: "search", datasetId };
    if (parts[2] === "a" && parts[3] && parts[4]) {
      return { name: "artifact", datasetId, artifactId: parts[3], versionId: parts[4] };
    }
    if (parts[2] === "dossier" && parts[3] && parts[4]) {
      return { name: "dossier", datasetId, artifactId: parts[3], versionId: parts[4] };
    }
    if (parts[2] === "inference" && parts[3] && parts[4]) {
      return { name: "inference", datasetId, artifactId: parts[3], versionId: parts[4] };
    }
    return { name: "workspace", datasetId };
  }
  return { name: "datasets" };
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

export function go(path: string): void {
  window.location.hash = path;
}
